import { DOCUMENT } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  AfterViewInit,
  AfterViewChecked,
  SimpleChanges,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
  Inject,
  Renderer2,
} from '@angular/core';
import {
  ParsedMarkdownTableData,
  MIN_COLUMN_WIDTH,
  MIN_ROW_HEIGHT,
  RESIZE_MIN_DOCUMENTS_COLUMN,
  RESIZE_MIN_PROSE_COLUMN,
  isDocumentsHeavyColumn,
  isProseHeavyColumn,
  columnHeaderDensityClass,
  columnCellDensityClass,
  tableStructureKey,
  buildDefaultColumnWidths,
  parseMarkdownTableData,
  exportParsedTableToXlsx,
  sanitizeDownloadFileName,
  filterTableRows,
  getUniqueColumnCellValues,
  getTableCellFilterValue,
  getTableCellPlainText,
  hasActiveTableColumnFilters,
  type TableColumnFilters,
} from '../../utils/markdown-table.util';

const CELL_HTML_BR = /<br\s*\/?>/gi;
/** Must stay below `.rich-table-download-overlay` (10000). */
const FILTER_MENU_Z_INDEX = '9999';

export type RichTableLayoutMode = 'fill' | 'overflow';

@Component({
  selector: 'app-markdown-rich-table',
  templateUrl: './markdown-rich-table.component.html',
  styleUrls: ['./markdown-rich-table.component.scss'],
})
export class MarkdownRichTableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() tableMarkdown = '';
  @Input() tableData: ParsedMarkdownTableData | null = null;
  @Input() isStreaming = false;
  /** When true (RIA + agent_config.options.tableSearch), shows keyword search above the table. */
  @Input() enableTableSearch = false;
  /** When true (RIA + agent_config.options.tableColumnFilter), shows per-column filter menus. */
  @Input() enableTableColumnFilter = false;
  /** When false, hides the table download action. */
  @Input() enableTableDownload = true;
  /** When false, all filtered rows render without pagination. Default true. */
  @Input() isTablePaginationEnabled = true;
  @Output() tableDownloaded = new EventEmitter<string>();
  @Output() tableDownloadFailed = new EventEmitter<void>();

  @ViewChild('downloadOverlay') downloadOverlayRef?: ElementRef<HTMLElement>;
  @ViewChild('filterMenuPortal') filterMenuPortalRef?: ElementRef<HTMLElement>;
  @ViewChildren('headerCell') headerCells!: QueryList<ElementRef<HTMLTableCellElement>>;
  @ViewChildren('bodyRow') bodyRows!: QueryList<ElementRef<HTMLTableRowElement>>;

  columnWidths: number[] | null = null;
  layoutMode: RichTableLayoutMode = 'fill';
  resizingColumnIndex: number | null = null;
  rowHeightOverrides: Record<number, number> = {};
  resizingRowIndex: number | null = null;
  rowsPerPage = 10;
  pageIndex = 0;
  /** True once the user picks a page size, so we stop auto-pinning it to the max. */
  private userSelectedRowsPerPage = false;
  showDownloadModal = false;
  downloadFileName = 'table';
  searchQuery = '';
  /** columnIndex -> selected plain-text cell values */
  columnFilterSelections: Record<number, Set<string>> = {};
  openFilterColumnIndex: number | null = null;
  filterMenuSearchQuery = '';

  /** True only when rendered within the ria agent (ancestor .ria-agent); gates ria-only layout. */
  isRia = false;
  /** True when rendered within regulations (ancestor .regulations-agent / .regulations-agent-view). */
  isRegulations = false;

  private structureKey: string | null = null;
  private resizeCleanup: (() => void) | null = null;
  private scrollResizeObserver: ResizeObserver | null = null;
  private overlayAttached = false;
  private overlayResizeListener: (() => void) | null = null;
  private filterMenuAttached = false;
  private filterMenuTriggerEl: HTMLElement | null = null;
  private filterMenuRepositionListener: (() => void) | null = null;
  private filterMenuDismissClickListener: ((event: MouseEvent) => void) | null = null;
  private filterMenuDismissFocusListener: ((event: FocusEvent) => void) | null = null;

  constructor(
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  get canResizeColumns(): boolean {
    return !this.isStreaming && this.tableData !== null;
  }

  get canDownload(): boolean {
    return this.enableTableDownload && !this.isStreaming && this.getExportTableData() !== null;
  }

  get isDownloadFileNameValid(): boolean {
    return this.downloadFileName.trim().length > 0;
  }

  get totalRows(): number {
    return this.filteredRows.length;
  }

  get filteredRows(): readonly (readonly string[])[] {
    if (!this.tableData) {
      return [];
    }
    return filterTableRows(this.tableData, {
      searchQuery: this.isTableSearchActive ? this.searchQuery : '',
      columnFilters: this.activeColumnFilters,
      columnFilterCellValue: this.isRia ? getTableCellFilterValue : undefined,
    });
  }

  get activeColumnFilters(): TableColumnFilters {
    const filters: Record<number, ReadonlySet<string>> = {};
    for (const [colIndexStr, values] of Object.entries(this.columnFilterSelections)) {
      if (values.size > 0) {
        filters[Number(colIndexStr)] = values;
      }
    }
    return filters;
  }

  get hasActiveColumnFilters(): boolean {
    return hasActiveTableColumnFilters(this.activeColumnFilters);
  }

  get isTableSearchActive(): boolean {
    return this.showTableSearch && this.searchQuery.trim().length > 0;
  }

  get isTableFilterActive(): boolean {
    return this.isTableSearchActive || this.hasActiveColumnFilters;
  }

  get showTableSearch(): boolean {
    return this.enableTableSearch && this.isRia && !this.isStreaming && this.useStructuredTable;
  }

  get showTableColumnFilter(): boolean {
    return this.enableTableColumnFilter && this.isRia && !this.isStreaming && this.useStructuredTable;
  }

  get paginationSummaryLabel(): string {
    return this.isTableFilterActive ? 'Showing filtered results' : 'Showing all results';
  }

  get canResizeRows(): boolean {
    return this.canResizeColumns && this.totalRows >= 2;
  }

  get hasColumnWidthLayout(): boolean {
    return (
      this.tableData !== null &&
      this.columnWidths !== null &&
      this.columnWidths.length === this.tableData.columns.length
    );
  }

  get isFillLayout(): boolean {
    return this.layoutMode === 'fill';
  }

  get isOverflowLayout(): boolean {
    return this.layoutMode === 'overflow';
  }

  /** Min table width when overflow layout (intrinsic sum enables horizontal scroll). */
  get tableMinWidthPx(): number | null {
    if (!this.hasColumnWidthLayout || this.columnWidths === null || this.isFillLayout) {
      return null;
    }
    const widths = this.columnWidths;
    if (widths.length === 0) {
      return null;
    }
    const sum = widths.reduce((total, width) => total + width, 0);
    return Math.max(sum + 1, 720);
  }

  /** Col width: % in fill mode; fixed px in overflow mode. */
  columnWidthStyle(colIndex: number): string | null {
    if (this.columnWidths === null || colIndex < 0 || colIndex >= this.columnWidths.length) {
      return null;
    }
    if (this.isOverflowLayout || this.columnWidths.length === 1) {
      return `${this.columnWidths[colIndex]}px`;
    }
    const total = this.columnWidths.reduce((sum, width) => sum + width, 0);
    if (total <= 0) {
      return null;
    }
    const percent = (this.columnWidths[colIndex] / total) * 100;
    return `${percent}%`;
  }

  columnWidthPx(colIndex: number): number | null {
    if (this.columnWidths === null || colIndex < 0 || colIndex >= this.columnWidths.length) {
      return null;
    }
    if (this.isOverflowLayout || this.columnWidths.length === 1) {
      return this.columnWidths[colIndex];
    }
    return null;
  }

  get isResizingColumns(): boolean {
    return this.resizingColumnIndex !== null;
  }

  get isResizingRows(): boolean {
    return this.resizingRowIndex !== null;
  }

  get isResizing(): boolean {
    return this.isResizingColumns || this.isResizingRows;
  }

  /** Structured table + pagination whenever the pipe table is parseable (including during streaming). */
  get useStructuredTable(): boolean {
    return this.tableData !== null;
  }

  get shouldPaginate(): boolean {
    return this.isTablePaginationEnabled && this.tableData !== null && this.totalRows > 10;
  }

  /** RIA / regulations: scroll area grows with content up to 400px, then scrolls. */
  get shouldUseFixedViewport(): boolean {
    return (this.isRia || this.isRegulations) && this.useStructuredTable;
  }

  get rowsPerPageOptions(): number[] {
    const maxOption = Math.max(10, Math.ceil(this.totalRows / 10) * 10);
    return Array.from({ length: maxOption / 10 }, (_, i) => (i + 1) * 10);
  }

  private get defaultRowsPerPage(): number {
    return this.rowsPerPageOptions.at(-1) ?? 10;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRows / this.rowsPerPage));
  }

  get safePageIndex(): number {
    return Math.min(this.pageIndex, this.totalPages - 1);
  }

  get rangeStart(): number {
    return this.totalRows === 0 ? 0 : this.safePageIndex * this.rowsPerPage + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.safePageIndex + 1) * this.rowsPerPage, this.totalRows);
  }

  get pagedRows(): readonly (readonly string[])[] {
    if (!this.isTablePaginationEnabled) {
      return this.filteredRows;
    }
    const start = this.safePageIndex * this.rowsPerPage;
    return this.filteredRows.slice(start, start + this.rowsPerPage);
  }

  get isWideTable(): boolean {
    return (this.tableData?.columns.length ?? 0) > 5;
  }

  /** RIA: plain text cells while streaming; full markdown sanitization after stream ends. */
  get usePlainTextCells(): boolean {
    return this.isRia && this.isStreaming;
  }

  ngOnInit(): void {
    const hostEl = this.hostRef.nativeElement;
    this.isRia = hostEl.closest('.ria-agent') !== null;
    this.isRegulations =
      hostEl.closest('.regulations-agent') !== null ||
      hostEl.closest('.regulations-agent-view') !== null;
    this.syncStructureAndColumnWidths();
  }

  ngAfterViewInit(): void {
    this.observeScrollHostResize();
    this.scheduleLayoutSync();
    this.registerFilterMenuDismissListeners();
  }

  ngAfterViewChecked(): void {
    if (this.showDownloadModal) {
      this.attachDownloadOverlayIfReady();
    }
    if (this.openFilterColumnIndex !== null) {
      this.attachFilterMenuIfReady();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableData'] || changes['tableMarkdown']) {
      this.syncStructureAndColumnWidths();
    }
    if (changes['isStreaming'] && this.isStreaming) {
      this.resizingColumnIndex = null;
      this.resizingRowIndex = null;
    }
    if (changes['isStreaming'] && !this.isStreaming && this.tableData && this.columnWidths === null) {
      this.columnWidths = buildDefaultColumnWidths(this.tableData.columns);
    }
    if (changes['tableData'] || changes['isStreaming']) {
      this.scheduleLayoutSync();
    }
    if (changes['tableData']) {
      this.searchQuery = '';
      this.columnFilterSelections = {};
      this.closeFilterMenu();
      // Keep page size pinned to the max option (showing all rows) until the user chooses otherwise.
      if (this.isTablePaginationEnabled && !this.userSelectedRowsPerPage) {
        this.rowsPerPage = this.defaultRowsPerPage;
        this.pageIndex = 0;
      }
    }
    this.syncRowsPerPage();
    this.clampPageIndex();
  }

  ngOnDestroy(): void {
    this.scrollResizeObserver?.disconnect();
    this.scrollResizeObserver = null;
    this.unregisterFilterMenuDismissListeners();
    this.detachFilterMenu();
    this.clearResizeBodyStyles();
    this.resizeCleanup?.();
  }

  /** Apply structure key + default column widths when tableData is available. */
  private syncStructureAndColumnWidths(): void {
    const nextKey = tableStructureKey(this.tableData);
    if (nextKey !== this.structureKey) {
      this.structureKey = nextKey;
      if (this.tableData !== null) {
        this.resetTableState();
      }
    } else if (
      this.tableData &&
      (this.columnWidths === null || this.columnWidths.length !== this.tableData.columns.length)
    ) {
      this.columnWidths = buildDefaultColumnWidths(this.tableData.columns);
      this.scheduleLayoutSync();
    }
  }

  headerDensityClass(column: string): string {
    return columnHeaderDensityClass(column);
  }

  cellDensityClass(column: string): string {
    return columnCellDensityClass(column);
  }

  cellMarkdownSource(cell: string): string {
    return (cell ?? '').replace(CELL_HTML_BR, '  \n');
  }

  globalRowIndex(rowIndex: number): number {
    return this.safePageIndex * this.rowsPerPage + rowIndex;
  }

  rowOverridePx(globalRowIndex: number): number | undefined {
    return this.rowHeightOverrides[globalRowIndex];
  }

  rowSizedCellClass(globalRowIndex: number): string {
    return this.rowOverridePx(globalRowIndex) === undefined ? '' : ' rich-table__td--row-sized';
  }

  rowSizeStyle(globalRowIndex: number): Record<string, string> | undefined {
    const px = this.rowOverridePx(globalRowIndex);
    if (px === undefined) return undefined;
    return {
      height: `${px}px`,
      'min-height': `${px}px`,
      '--rich-table-row-h': `${px}px`,
    };
  }

  onRowsPerPageChange(size: number): void {
    this.dismissFilterMenuForRiaAction();
    const next = Number(size);
    this.rowsPerPage = Number.isNaN(next) ? 10 : next;
    this.userSelectedRowsPerPage = true;
    this.pageIndex = 0;
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (value === this.searchQuery) {
      return;
    }
    this.dismissFilterMenuForRiaAction();
    this.searchQuery = value;
    this.pageIndex = 0;
    this.clampPageIndex();
  }

  toggleFilterMenu(columnIndex: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.openFilterColumnIndex === columnIndex) {
      this.closeFilterMenu();
      return;
    }
    this.detachFilterMenu();
    this.openFilterColumnIndex = columnIndex;
    this.filterMenuSearchQuery = '';
    this.filterMenuTriggerEl = event.currentTarget as HTMLElement;
  }

  closeFilterMenu(): void {
    this.detachFilterMenu();
    this.openFilterColumnIndex = null;
    this.filterMenuSearchQuery = '';
    this.filterMenuTriggerEl = null;
  }

  isFilterTriggerVisible(columnIndex: number): boolean {
    return this.isColumnFilterActive(columnIndex) || this.openFilterColumnIndex === columnIndex;
  }

  onFilterMenuSearchInput(event: Event): void {
    this.filterMenuSearchQuery = (event.target as HTMLInputElement).value;
  }

  onFilterMenuSearchKeydown(event: KeyboardEvent): void {
    if (this.openFilterColumnIndex === null) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeFilterMenu();
      return;
    }
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    const columnIndex = this.openFilterColumnIndex;
    if (!this.filterMenuSearchQuery.trim()) {
      this.clearColumnFilterSelections(columnIndex);
      return;
    }
    this.selectFilteredColumnFilterValues(columnIndex);
  }

  private clearColumnFilterSelections(columnIndex: number): void {
    if (!this.columnFilterSelections[columnIndex]?.size) {
      return;
    }
    const updated = { ...this.columnFilterSelections };
    delete updated[columnIndex];
    this.columnFilterSelections = updated;
    this.pageIndex = 0;
    this.clampPageIndex();
  }

  private selectFilteredColumnFilterValues(columnIndex: number): void {
    const filtered = this.getColumnFilterValuesMatchingSearch(columnIndex);
    if (filtered.length === 0) {
      return;
    }
    const next = new Set(this.columnFilterSelections[columnIndex] ?? []);
    for (const value of filtered) {
      next.add(value);
    }
    this.columnFilterSelections = { ...this.columnFilterSelections, [columnIndex]: next };
    this.pageIndex = 0;
    this.clampPageIndex();
  }

  private getColumnFilterCellValueReader(): (cell: string) => string {
    return this.isRia ? getTableCellFilterValue : getTableCellPlainText;
  }

  private getColumnFilterValuesMatchingSearch(columnIndex: number): readonly string[] {
    if (!this.tableData) {
      return [];
    }
    const query = this.filterMenuSearchQuery.trim().toLowerCase();
    if (!query) {
      return [];
    }
    return getUniqueColumnCellValues(
      this.tableData,
      columnIndex,
      this.getColumnFilterCellValueReader(),
    ).filter((value) => value.toLowerCase().includes(query));
  }

  isColumnFilterActive(columnIndex: number): boolean {
    return (this.columnFilterSelections[columnIndex]?.size ?? 0) > 0;
  }

  isColumnFilterValueSelected(columnIndex: number, value: string): boolean {
    return this.columnFilterSelections[columnIndex]?.has(value) ?? false;
  }

  toggleColumnFilterValue(columnIndex: number, value: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const next = new Set(this.columnFilterSelections[columnIndex] ?? []);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    const updated = { ...this.columnFilterSelections };
    if (next.size === 0) {
      delete updated[columnIndex];
    } else {
      updated[columnIndex] = next;
    }
    this.columnFilterSelections = updated;
    this.pageIndex = 0;
    this.clampPageIndex();
  }

  onFilterOptionKeydown(event: KeyboardEvent, columnIndex: number, value: string): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      this.toggleColumnFilterValue(columnIndex, value, event);
    }
  }

  getColumnFilterOptions(columnIndex: number): readonly string[] {
    if (!this.tableData) {
      return [];
    }
    const query = this.filterMenuSearchQuery.trim().toLowerCase();
    const filtered = query
      ? this.getColumnFilterValuesMatchingSearch(columnIndex)
      : getUniqueColumnCellValues(
          this.tableData,
          columnIndex,
          this.getColumnFilterCellValueReader(),
        );
    const selected = this.columnFilterSelections[columnIndex] ?? new Set<string>();
    const selectedValues = filtered.filter((value) => selected.has(value));
    const unselectedValues = filtered.filter((value) => !selected.has(value));
    return [...selectedValues, ...unselectedValues];
  }

  columnFilterDisplayValue(value: string): string {
    return value.length > 0 ? value : '(empty)';
  }

  private registerFilterMenuDismissListeners(): void {
    if (this.filterMenuDismissClickListener) {
      return;
    }

    this.filterMenuDismissClickListener = (event: MouseEvent) =>
      this.onFilterMenuDismissInteraction(event);
    this.filterMenuDismissFocusListener = (event: FocusEvent) =>
      this.onFilterMenuDismissInteraction(event);
    this.document.addEventListener('click', this.filterMenuDismissClickListener, true);
    this.document.addEventListener('focusin', this.filterMenuDismissFocusListener, true);
  }

  private unregisterFilterMenuDismissListeners(): void {
    if (this.filterMenuDismissClickListener) {
      this.document.removeEventListener('click', this.filterMenuDismissClickListener, true);
      this.filterMenuDismissClickListener = null;
    }
    if (this.filterMenuDismissFocusListener) {
      this.document.removeEventListener('focusin', this.filterMenuDismissFocusListener, true);
      this.filterMenuDismissFocusListener = null;
    }
  }

  private onFilterMenuDismissInteraction(event: Event): void {
    if (!this.isRia || this.openFilterColumnIndex === null) {
      return;
    }

    const target = event.target as Node | null;
    if (!target || this.isFilterMenuInteractionTarget(target)) {
      return;
    }

    this.closeFilterMenu();
  }

  private isFilterMenuInteractionTarget(target: Node): boolean {
    const menu = this.filterMenuPortalRef?.nativeElement;
    return !!(menu?.contains(target) || this.filterMenuTriggerEl?.contains(target));
  }

  /** Closes an open column filter when the user performs another RIA table action. */
  private dismissFilterMenuForRiaAction(): void {
    if (this.isRia && this.openFilterColumnIndex !== null) {
      this.closeFilterMenu();
    }
  }

  private attachFilterMenuIfReady(): void {
    const menu = this.filterMenuPortalRef?.nativeElement;
    if (!menu || this.filterMenuAttached) {
      this.syncFilterMenuPosition();
      return;
    }

    this.syncFilterMenuPosition();
    this.renderer.appendChild(this.document.body, menu);
    this.filterMenuAttached = true;
    this.filterMenuRepositionListener = () => this.syncFilterMenuPosition();
    window.addEventListener('scroll', this.filterMenuRepositionListener, true);
    window.addEventListener('resize', this.filterMenuRepositionListener);
  }

  private detachFilterMenu(): void {
    if (this.filterMenuRepositionListener) {
      window.removeEventListener('scroll', this.filterMenuRepositionListener, true);
      window.removeEventListener('resize', this.filterMenuRepositionListener);
      this.filterMenuRepositionListener = null;
    }

    const menu = this.filterMenuPortalRef?.nativeElement;
    if (this.filterMenuAttached && menu?.parentNode === this.document.body) {
      this.renderer.removeChild(this.document.body, menu);
    }
    this.filterMenuAttached = false;
  }

  private syncFilterMenuPosition(): void {
    const trigger = this.filterMenuTriggerEl;
    const menu = this.filterMenuPortalRef?.nativeElement;
    if (!trigger || !menu) {
      return;
    }

    const headerCell = trigger.closest('th');
    const anchorRect = headerCell?.getBoundingClientRect() ?? trigger.getBoundingClientRect();
    const menuWidth = Math.max(200, Math.round(anchorRect.width));
    let left = Math.round(anchorRect.left);
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    const top = Math.round(anchorRect.bottom + 4);

    menu.style.position = 'fixed';
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.style.width = `${menuWidth}px`;
    menu.style.minWidth = `${menuWidth}px`;
    menu.style.zIndex = FILTER_MENU_Z_INDEX;
  }

  previousPage(): void {
    this.dismissFilterMenuForRiaAction();
    this.pageIndex = Math.max(this.pageIndex - 1, 0);
  }

  nextPage(): void {
    this.dismissFilterMenuForRiaAction();
    this.pageIndex = Math.min(this.pageIndex + 1, this.totalPages - 1);
  }

  openDownloadModal(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canDownload) {
      return;
    }
    this.dismissFilterMenuForRiaAction();
    this.downloadFileName = 'table';
    this.showDownloadModal = true;
  }

  closeDownloadModal(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.detachDownloadOverlay();
    this.showDownloadModal = false;
  }

  onDownloadOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeDownloadModal();
    }
  }

  confirmDownload(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.isDownloadFileNameValid) {
      return;
    }
    const tableData = this.getExportTableData();
    if (!tableData) {
      this.tableDownloadFailed.emit();
      this.closeDownloadModal();
      return;
    }
    const fileName = sanitizeDownloadFileName(this.downloadFileName);
    try {
      exportParsedTableToXlsx(tableData, fileName);
      this.tableDownloaded.emit(fileName);
      this.closeDownloadModal();
    } catch {
      this.tableDownloadFailed.emit();
      this.closeDownloadModal();
    }
  }

  private attachDownloadOverlayIfReady(): void {
    const overlay = this.downloadOverlayRef?.nativeElement;
    if (!this.showDownloadModal || this.overlayAttached || !overlay) {
      return;
    }

    this.syncOverlayBounds();
    this.renderer.appendChild(this.document.body, overlay);
    this.overlayAttached = true;
    this.overlayResizeListener = () => this.syncOverlayBounds();
    window.addEventListener('resize', this.overlayResizeListener);
    overlay.focus();
  }

  private detachDownloadOverlay(): void {
    if (this.overlayResizeListener) {
      window.removeEventListener('resize', this.overlayResizeListener);
      this.overlayResizeListener = null;
    }

    const overlay = this.downloadOverlayRef?.nativeElement;
    if (this.overlayAttached && overlay?.parentNode === this.document.body) {
      this.renderer.removeChild(this.document.body, overlay);
    }

    this.overlayAttached = false;
    this.document.documentElement.style.removeProperty('--rich-table-overlay-left');
  }

  /** Offset overlay so the agent sidebar stays visible and undimmed. */
  private syncOverlayBounds(): void {
    const leftOffset = this.getAgentSidebarOffset();
    this.document.documentElement.style.setProperty('--rich-table-overlay-left', `${leftOffset}px`);
  }

  private getAgentSidebarOffset(): number {
    const sidebar = this.document.querySelector('.sidebar-panel') as HTMLElement | null;
    if (!sidebar || sidebar.classList.contains('sidebar-collapsed')) {
      return 0;
    }
    const width = sidebar.getBoundingClientRect().width;
    return width > 0 ? Math.round(width) : 0;
  }

  private getExportTableData(): ParsedMarkdownTableData | null {
    if (this.tableData) {
      if (this.isTableFilterActive) {
        return {
          columns: this.tableData.columns,
          rows: this.filteredRows,
        };
      }
      return this.tableData;
    }
    if (this.tableMarkdown.trim().length === 0) {
      return null;
    }
    return parseMarkdownTableData(this.tableMarkdown);
  }

  resetColumnWidths(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.columnWidths = this.tableData ? buildDefaultColumnWidths(this.tableData.columns) : null;
    this.resizingColumnIndex = null;
    this.scheduleLayoutSync();
  }

  resetRowHeights(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.rowHeightOverrides = {};
    this.resizingRowIndex = null;
  }

  startColumnResize(columnIndex: number, event: PointerEvent): void {
    if (!this.canResizeColumns || !this.tableData || (event.button !== undefined && event.button !== 0)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.resizingRowIndex = null;

    const target = event.currentTarget as HTMLElement;
    const startX = event.clientX;
    const pointerId = event.pointerId;
    const initialWidths = this.resolveCurrentColumnWidths();
    const startWidth = initialWidths[columnIndex] ?? MIN_COLUMN_WIDTH;

    this.columnWidths = [...initialWidths];
    this.resizingColumnIndex = columnIndex;
    this.applyResizeBodyStyles(true, false);

    try {
      target.setPointerCapture(pointerId);
    } catch {
      // pointer may be inactive
    }

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const deltaX = moveEvent.clientX - startX;
      const nextWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + deltaX));
      const base = this.columnWidths ?? [...initialWidths];
      if (base[columnIndex] !== nextWidth) {
        const updated = [...base];
        const delta = nextWidth - updated[columnIndex];
        updated[columnIndex] = nextWidth;
        const lastIndex = updated.length - 1;
        if (this.layoutMode === 'fill' && lastIndex > columnIndex) {
          updated[lastIndex] = Math.max(MIN_COLUMN_WIDTH, updated[lastIndex] - delta);
        }
        this.columnWidths = updated;
        this.recomputeLayoutMode();
      }
    };

    const stopResize = (endEvent: PointerEvent) => {
      if (endEvent.pointerId !== pointerId) return;
      target.removeEventListener('pointermove', handlePointerMove);
      target.removeEventListener('pointerup', stopResize);
      target.removeEventListener('pointercancel', stopResize);
      try {
        if (target.hasPointerCapture?.(pointerId)) {
          target.releasePointerCapture(pointerId);
        }
      } catch {
        // ignore
      }
      this.resizingColumnIndex = null;
      this.clearResizeBodyStyles();
      this.recomputeLayoutMode();
    };

    target.addEventListener('pointermove', handlePointerMove);
    target.addEventListener('pointerup', stopResize);
    target.addEventListener('pointercancel', stopResize);
  }

  startRowResize(globalRowIndex: number, event: PointerEvent): void {
    if (
      !this.canResizeRows ||
      !this.tableData ||
      globalRowIndex < 0 ||
      globalRowIndex >= this.totalRows - 1 ||
      (event.button !== undefined && event.button !== 0)
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.resizingColumnIndex = null;

    const target = event.currentTarget as HTMLElement;
    const startY = event.clientY;
    const pointerId = event.pointerId;
    const rowEl = this.getBodyRowElement(globalRowIndex);
    const measured = rowEl?.getBoundingClientRect().height;
    const rawHeight =
      measured !== undefined && Number.isFinite(measured)
        ? measured
        : (this.rowHeightOverrides[globalRowIndex] ?? MIN_ROW_HEIGHT);
    const startHeight = Math.max(MIN_ROW_HEIGHT, Math.round(rawHeight));

    this.resizingRowIndex = globalRowIndex;
    this.applyResizeBodyStyles(false, true);

    try {
      target.setPointerCapture(pointerId);
    } catch {
      // ignore
    }

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const deltaY = moveEvent.clientY - startY;
      const nextHeight = Math.max(MIN_ROW_HEIGHT, Math.round(startHeight + deltaY));
      if (this.rowHeightOverrides[globalRowIndex] !== nextHeight) {
        this.rowHeightOverrides = { ...this.rowHeightOverrides, [globalRowIndex]: nextHeight };
      }
    };

    const stopResize = (endEvent: PointerEvent) => {
      if (endEvent.pointerId !== pointerId) return;
      target.removeEventListener('pointermove', handlePointerMove);
      target.removeEventListener('pointerup', stopResize);
      target.removeEventListener('pointercancel', stopResize);
      try {
        if (target.hasPointerCapture?.(pointerId)) {
          target.releasePointerCapture(pointerId);
        }
      } catch {
        // ignore
      }
      this.resizingRowIndex = null;
      this.clearResizeBodyStyles();
    };

    target.addEventListener('pointermove', handlePointerMove);
    target.addEventListener('pointerup', stopResize);
    target.addEventListener('pointercancel', stopResize);
  }

  private resetTableState(): void {
    this.columnWidths = this.tableData ? buildDefaultColumnWidths(this.tableData.columns) : null;
    this.resizingColumnIndex = null;
    this.rowHeightOverrides = {};
    this.resizingRowIndex = null;
    this.rowsPerPage = this.defaultRowsPerPage;
    this.userSelectedRowsPerPage = false;
    this.pageIndex = 0;
    this.searchQuery = '';
    this.columnFilterSelections = {};
    this.closeFilterMenu();
    this.scheduleLayoutSync();
  }

  private syncRowsPerPage(): void {
    const options = this.rowsPerPageOptions;
    if (options.length === 0) {
      return;
    }
    if (!options.includes(this.rowsPerPage)) {
      this.rowsPerPage = options.at(-1) ?? 10;
      this.pageIndex = 0;
    }
  }

  private clampPageIndex(): void {
    if (this.pageIndex !== this.safePageIndex) {
      this.pageIndex = this.safePageIndex;
    }
  }

  private intrinsicColumnSum(): number {
    if (!this.columnWidths?.length) {
      return 0;
    }
    return this.columnWidths.reduce((total, width) => total + width, 0);
  }

  private getScrollHostWidth(): number {
    const scrollEl = this.hostRef.nativeElement.querySelector('.rich-table__scroll') as HTMLElement | null;
    return scrollEl?.clientWidth ?? 0;
  }

  private recomputeLayoutMode(): void {
    if (!this.hasColumnWidthLayout) {
      return;
    }
    const intrinsic = this.intrinsicColumnSum();
    const hostWidth = this.getScrollHostWidth();
    if (hostWidth <= 0 || intrinsic <= 0) {
      return;
    }
    this.layoutMode = intrinsic > hostWidth ? 'overflow' : 'fill';
    if (this.layoutMode === 'fill' && intrinsic < hostWidth) {
      this.scaleWeightsToHost(hostWidth);
    }
  }

  private scaleWeightsToHost(hostWidth: number): void {
    if (!this.columnWidths?.length) {
      return;
    }
    const sum = this.intrinsicColumnSum();
    if (sum <= 0 || sum >= hostWidth) {
      return;
    }
    const scale = hostWidth / sum;
    this.columnWidths = this.columnWidths.map((width) =>
      Math.max(MIN_COLUMN_WIDTH, Math.round(width * scale)),
    );
    this.layoutMode = 'fill';
  }

  private scheduleLayoutSync(): void {
    queueMicrotask(() => this.recomputeLayoutMode());
  }

  private observeScrollHostResize(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const scrollEl = this.hostRef.nativeElement.querySelector('.rich-table__scroll');
    if (!scrollEl) {
      return;
    }
    this.scrollResizeObserver?.disconnect();
    this.scrollResizeObserver = new ResizeObserver(() => this.recomputeLayoutMode());
    this.scrollResizeObserver.observe(scrollEl);
  }

  private measureNaturalColumnWidth(columnIndex: number, columnName: string): number {
    let maxWidth = MIN_COLUMN_WIDTH;

    const headerCell = this.headerCells?.toArray()[columnIndex]?.nativeElement;
    if (headerCell) {
      maxWidth = Math.max(maxWidth, headerCell.offsetWidth, headerCell.scrollWidth);
    }

    for (const rowRef of this.bodyRows?.toArray() ?? []) {
      const bodyCell = rowRef.nativeElement.cells.item(columnIndex) as HTMLTableCellElement | null;
      if (bodyCell) {
        maxWidth = Math.max(maxWidth, bodyCell.offsetWidth, bodyCell.scrollWidth);
      }
    }

    if (isDocumentsHeavyColumn(columnName)) {
      maxWidth = Math.max(maxWidth, RESIZE_MIN_DOCUMENTS_COLUMN);
    }
    if (isProseHeavyColumn(columnName)) {
      maxWidth = Math.max(maxWidth, RESIZE_MIN_PROSE_COLUMN);
    }
    return maxWidth;
  }

  private resolveCurrentColumnWidths(): number[] {
    if (!this.tableData) return [];

    if (this.layoutMode === 'fill') {
      return this.tableData.columns.map((columnName, index) =>
        this.measureNaturalColumnWidth(index, columnName),
      );
    }

    if (this.columnWidths !== null && this.columnWidths.length === this.tableData.columns.length) {
      return [...this.columnWidths];
    }

    return this.tableData.columns.map((columnName, index) =>
      this.measureNaturalColumnWidth(index, columnName),
    );
  }

  private getBodyRowElement(globalRowIndex: number): HTMLTableRowElement | undefined {
    const rows = this.bodyRows?.toArray() ?? [];
    const pageStart = this.safePageIndex * this.rowsPerPage;
    const localIndex = globalRowIndex - pageStart;
    return rows[localIndex]?.nativeElement;
  }

  private applyResizeBodyStyles(_resizingColumns: boolean, resizingRows: boolean): void {
    document.body.style.userSelect = 'none';
    document.body.style.cursor = resizingRows ? 'row-resize' : 'col-resize';
    this.resizeCleanup = () => this.clearResizeBodyStyles();
  }

  private clearResizeBodyStyles(): void {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    this.resizeCleanup = null;
  }
}
